/**
 * O que a tela dos índices regulatórios (`OB-05`, ADR-069) usa e o teste confere, fora do
 * componente — a recarga a quente do Vite só funciona em arquivo que exporta só componente.
 *
 * Nada aqui calcula índice: o percentual vem do servidor **truncado** (94,96% chega 94.9), e a
 * tela só troca o ponto pela vírgula. Arredondar de novo aqui poderia mostrar 95,0% num índice
 * abaixo da meta.
 */
import type { IndiceNaTela, InoperanciaNaTela } from "../api/tipos";
import type { Significado } from "../componentes/base";

type Situacao = IndiceNaTela["situacao"];

/** A cor de cada situação: abaixo é vermelho, em risco é âmbar, atende é verde. */
export const COR_DA_SITUACAO: Record<Situacao, Significado> = {
  ATENDE: "verde",
  EM_RISCO: "amarelo",
  ABAIXO: "vermelho",
  SEM_BASE: "cinza",
  SEM_META: "cinza",
  SEM_FONTE: "cinza",
};

/** O percentual do servidor, com vírgula — sem arredondar de novo. */
export function percentualDoIndice(texto: string | null | undefined): string {
  if (texto == null) return "—";
  return `${texto.replace(".", ",")}%`;
}

/** "referência federal 95%", "meta 95%" ou o limite que falta. */
export function metaDoIndice(indice: IndiceNaTela): string {
  const meta = indice.meta;
  if (!meta) return "sem meta";
  if (meta.minimo == null) return "limite do poder concedente, não recebido";
  const minimo = percentualDoIndice(String(meta.minimo));
  return meta.referencia ? `referência federal ${minimo}` : `meta ${minimo}`;
}

/** Uma duração em segundos como quem lê um relatório de operação. */
export function duracao(segundos: number): string {
  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) return `${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24)
    return `${horas} h ${String(minutos % 60).padStart(2, "0")} min`;
  return `${Math.floor(horas / 24)} d ${horas % 24} h`;
}

/**
 * A fila da SENATRAN com o que faltou ao índice: o recorte, o período do mês até o corte da
 * base e a concessão (e o pórtico) — o clique que abre exatamente o que o número contou
 * (`AC14`).
 */
export function enderecoDaFalta(
  indice: IndiceNaTela,
  desde: string,
  concessao: string,
  praca?: string,
): string | null {
  if (!indice.recorte || !indice.faltas) return null;
  const p = new URLSearchParams({
    recorte: indice.recorte,
    desde,
    ate: indice.ate,
    concessao_id: concessao,
  });
  if (praca) p.set("praca_id", praca);
  return `/senatran?${p.toString()}`;
}

/** O `datetime-local` vem sem fuso e é lido no do navegador; vai ao servidor com ele. */
export function paraOServidor(local: string): string {
  return new Date(local).toISOString();
}

/** O instante no formato do `datetime-local`, no fuso do navegador. */
export function paraOCampo(iso: string): string {
  const d = new Date(iso);
  const doisDigitos = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${doisDigitos(d.getMonth() + 1)}-${doisDigitos(d.getDate())}` +
    `T${doisDigitos(d.getHours())}:${doisDigitos(d.getMinutes())}`
  );
}

export const ROTULO_DA_INOPERANCIA: Record<
  InoperanciaNaTela["situacao"],
  { texto: string; cor: Significado }
> = {
  SUSPEITA: { texto: "Suspeita", cor: "amarelo" },
  CONFIRMADA: { texto: "Confirmada", cor: "vermelho" },
  DESCARTADA: { texto: "Descartada", cor: "cinza" },
};

/** O aviso ao regulador que ainda falta registrar, na falha crítica confirmada. */
export function avisoPendente(x: InoperanciaNaTela): boolean {
  return (
    x.situacao === "CONFIRMADA" &&
    x.critica &&
    x.avisado_em == null &&
    x.avisar_ate != null
  );
}

/** Dá para registrar o aviso: a falha crítica confirmada que ainda não tem — com prazo do
 *  regime ou sem (o regime sem prazo também aceita o registro). */
export function podeAvisar(x: InoperanciaNaTela): boolean {
  return x.situacao === "CONFIRMADA" && x.critica && x.avisado_em == null;
}

/** Os rótulos curtos das colunas da tabela por pórtico, na ordem dos índices. */
export const COLUNA_DO_INDICE: Record<IndiceNaTela["indice"], string> = {
  LEITURA_DE_PLACA: "Leitura",
  PROCESSAMENTO: "Processamento",
  PAGAVEL_EM_2H: "Pagar em 2 h",
  PAGAVEL_EM_24H: "Pagar em 24 h",
  DISPONIBILIDADE: "Disponibilidade",
  APROVEITAMENTO: "Aproveitamento",
};
